// @flow

import * as React from 'react';
import styled from 'styled-components';
import { InputOption } from '../../internal/internal-typography';
import Theme from './theme';
import { Grid } from '../../layouts/grid';
/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
import type { ReactComponentStyled } from 'styled-components';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

/*
  Adapted from this blog post:
  https://css-tricks.com/custom-styling-form-inputs-with-modern-css-features/

  One commenter converted the sass in the post into react and styled components:
  https://codesandbox.io/s/custom-css-only-form-inputs-react-hofx5?file=/src/Checkbox.tsx
 */

type CheckboxProps = StandaloneCheckboxProps & {
  /** Every checkbox should have an associated label for accessibility */
  label: string,
  value: string,
  ...DataProvidesProps,
};

type StandaloneCheckboxProps = {
  /** prevent interacting with the checkbox */
  disabled?: boolean,
  /** Current state */
  checked: boolean,
  /**
   * On change callback
   *
   * @param checked the new state of the checkbox (a shortcut for event.currentTarget.checked)
   * @param e the full event. */
  onChange: (checked: boolean, e?: SyntheticEvent<HTMLInputElement>) => void,
};

const Checkbox = (props: CheckboxProps) => {
  const { disabled, label, ...rest } = props;

  return (
    <StyledLabel disabled={disabled} {...getDataProvidesProps(props)}>
      <Grid container spacing={2}>
        <Grid item>
          <StandaloneCheckbox disabled={disabled} {...rest} />
        </Grid>
        <Grid item xs>
          {label}
        </Grid>
      </Grid>
    </StyledLabel>
  );
};
Checkbox.displayName = 'Checkbox';
Checkbox.defaultProps = { 'data-provides': 'checkbox' };

/**
 * This checkbox is not documented in the storybook because it should not be
 * used by consumers directly. Instead it should be used as part of other
 * seedling components that take into account accessibility.
 */
export const StandaloneCheckbox = (props: StandaloneCheckboxProps) => {
  const { onChange, ...rest } = props;

  let wrappingOnChange;

  if (onChange) {
    wrappingOnChange = (e) => {
      onChange(e.currentTarget.checked, e);
    };
  }

  return (
    <CheckboxWrapper>
      <input type="checkbox" {...rest} onChange={wrappingOnChange} />
      <VisualCheckbox />
    </CheckboxWrapper>
  );
};
StandaloneCheckbox.defaultProps = {
  disabled: false,
};
StandaloneCheckbox.displayName = 'StandaloneCheckbox';

const VisualCheckbox = () => {
  return (
    <span>
      <Checkmark />
    </span>
  );
};

const StyledLabel: ReactComponentStyled<{}> = styled(InputOption)`
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
  cursor: not-allowed;
  color: ${Theme.disabled.labelColor};
  `};
`;

const Checkmark = () => {
  return (
    <svg
      width="13"
      height="9"
      viewBox="0 0 13 9"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.25119 8.5416C4.12184 8.54174 3.99765 8.49086 3.90559 8.4L0.419193 4.976C0.370197 4.93135 0.330711 4.87729 0.303098 4.81703C0.275485 4.75677 0.260312 4.69156 0.258488 4.6253C0.256663 4.55904 0.268224 4.49309 0.292479 4.4314C0.316734 4.36972 0.353185 4.31356 0.399649 4.26628C0.446114 4.21901 0.501638 4.1816 0.562898 4.15629C0.624158 4.13097 0.689895 4.11828 0.756176 4.11896C0.822457 4.11964 0.887919 4.13369 0.948645 4.16026C1.00937 4.18683 1.06411 4.22538 1.10959 4.2736L3.96479 7.0736C4.03956 7.14689 4.14009 7.18794 4.24479 7.18794C4.34949 7.18794 4.45002 7.14689 4.52479 7.0736L10.9504 0.639997C11.0433 0.550349 11.1676 0.500725 11.2967 0.501795C11.4258 0.502864 11.5493 0.554541 11.6407 0.645715C11.7321 0.73689 11.784 0.860283 11.7854 0.989364C11.7868 1.11845 11.7374 1.24291 11.648 1.336L4.59919 8.4C4.55339 8.44539 4.49907 8.48127 4.43934 8.50557C4.37961 8.52987 4.31567 8.54212 4.25119 8.5416Z"
        stroke="#008561"
        strokeWidth="0.5"
      />
    </svg>
  );
};

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const CheckboxWrapper = styled.span`
  input {
    // Hide the browser default checkbox in a purely visual way (will still be recognized by screen readers)
    appearance: none;
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  span {
    background-color: ${Theme.default.backgroundColor};
    display: inline-block;
    position: relative;
    height: 1.125rem;
    width: 1.125rem;
    border: 1px solid ${Theme.default.inputColor};
    border-radius: 5px;
    box-sizing: border-box;
    top: 0.1875rem;
    left: 0;

    svg {
      fill: ${Theme.default.inputColor};
      stroke: ${Theme.default.inputColor};
      opacity: 0;
      position: absolute;
      top: 0.2125rem;
      left: 0.125rem;
    }
  }

  input:checked + span > svg {
    opacity: 1;
  }

  input:focus + span {
    box-shadow: 0px 0px 5px 0px ${Theme.default.inputColor}99;
  }

  input:hover + span {
    border-color: ${Theme.hover.inputColor};

    > svg path {
      fill: ${Theme.hover.inputColor};
      stroke: ${Theme.hover.inputColor};
    }
  }

  input:active + span {
    border-color: ${Theme.active.inputColor};

    > svg path {
      fill: ${Theme.active.inputColor};
      stroke: ${Theme.active.inputColor};
    }
  }

  input:disabled + span {
    border-color: ${Theme.disabled.inputColor};
    cursor: not-allowed;

    > svg path {
      fill: ${Theme.disabled.inputColor};
      stroke: ${Theme.disabled.inputColor};
    }
  }
`;

export default Checkbox;
